import { useState } from 'react';
import { Link } from 'react-router-dom';

function Layout({ children }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const handleMenuClose = () => setIsMenuOpen(false);

  const handleKarLogin = () => {
    // Just go to the /admin route – AdminPortal handles Cognito
    window.location.href = "/admin";
  };

  return (
    <div className="flex flex-col min-h-screen bg-black text-white">
      {/* Navbar */}
      <header
        className="relative container my-2 mx-auto bg-black py-3 sticky top-0 z-50 before:content-[''] before:absolute before:left-0 before:bottom-0 before:w-full before:h-[5px] before:bg-[#1466D4]"
      >
        <div className="pr-4 flex items-end justify-between h-20">
          {/* Logo */}
          <Link to="/" className="block">
            <img
              src="/logo.png"
              alt="KAR Shuttle"
              className="h-16 md:h-20 lg:h-24 w-auto -mb-3"
            />
          </Link>

          {/* Right Section */}
          <nav className="hidden lg:flex flex-1 justify-end space-x-4 text-white text-lg font-semibold">
            <Link
              to="/#destinations"
              className="text-white no-underline hover:no-underline hover:text-[#1466D4] cursor-pointer transition-colors duration-200"
            >
              Destinations & Fleet
            </Link>
            <Link
              to="/faqs"
              className="text-white no-underline hover:no-underline hover:text-[#1466D4] cursor-pointer transition-colors duration-200"
            >
              FAQs
            </Link>
            <Link
              to="/contact"
              className="text-white no-underline hover:no-underline hover:text-[#1466D4] cursor-pointer transition-colors duration-200"
            >
              Contact Us
            </Link>
          </nav>

          {/* Hamburger / Close Icon */}
          <button
            className="text-white lg:hidden transition-transform duration-200"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              // ✕ (Close)
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="w-7 h-7"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              // ☰ (Hamburger)
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="w-7 h-7"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Dropdown Menu for Smaller Screens */}
        {isMenuOpen && (
          <nav className="absolute top-14 left-0 w-full bg-black mt-16 lg:hidden">
            <ul className="flex flex-col items-center space-y-2 py-4 text-white">
              <li>
                <Link
                  to="/#destinations"
                  className="cursor-pointer hover:text-blue-600"
                  onClick={handleMenuClose}
                >
                  Destinations & Fleet
                </Link>
              </li>
              <li>
                <Link
                  to="/faqs"
                  className="cursor-pointer hover:text-blue-600"
                  onClick={handleMenuClose}
                >
                  FAQs
                </Link>
              </li>
              <li>
                <Link
                  to="/private"
                  className="cursor-pointer hover:text-blue-600"
                  onClick={handleMenuClose}
                >
                  Private Bookings
                </Link>
              </li>
              <li>
                <Link
                  to="/memberships"
                  className="cursor-pointer hover:text-blue-600"
                  onClick={handleMenuClose}
                >
                  Memberships
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="cursor-pointer hover:text-blue-600"
                  onClick={handleMenuClose}
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">{children}</main>

      <footer className="container mx-auto px-4 bg-black py-6 border-t-[5px] border-[#1466D4]">
        {/* Desktop Layout */}
        <div className="hidden lg:flex lg:flex-row md:items-center lg:justify-between text-sm">
          {/* Left Section: Copyright */}
          <div className="text-[10px]">
            © 2025 KAR Shuttles Ltd. All rights reserved.
          </div>

          {/* Middle Section: Policy Links */}
          <nav className="absolute left-1/2 transform -translate-x-1/2 text-xs lg:text-sm font-semibold">
            <a
              href="/franchise"
              target="_blank"
              className="hover:underline mx-2"
            >
              Franchise
            </a>
            <span className="text-white">|</span>
            <a
              href="https://drive.google.com/file/d/19AEqpT_aszYBo8RxES6KsIzH_RiOFH-i/view?usp=sharing"
              download
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline mx-2"
            >
              Privacy Policy
            </a>
            <span className="text-white">|</span>
            <a
              href="https://drive.google.com/file/d/184xj4cMqj9xV8sWHvNnCyhVtKaVAOHfD/view?usp=sharing"
              download
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline mx-2"
            >
              Terms of Service
            </a>
          </nav>

          {/* Right Section: Social + KAR Login */}
          <div className="flex justify-end space-x-4">
            <a
              href="https://www.instagram.com/karshuttle"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/instagram_logo.svg"
                alt="Instagram"
                className="w-5 h-5 md:w-6 md:h-6"
              />
            </a>
            <button
              onClick={handleKarLogin}
              className="inline-flex items-center rounded-full border border-white/30 px-4 py-1.5 text-sm font-semibold hover:bg-white hover:text-black transition"
              title="Admin Portal"
              type="button"
            >
              KAR Login
            </button>
          </div>
        </div>

        {/* Mobile/Tablet Layout */}
        <div className="flex flex-col lg:hidden items-center justify-between text-sm">
          {/* Top Row: Policy Links */}
          <nav className="flex flex-wrap justify-center space-x-4 space-t-4 text-xs font-semibold mb-2">
            <a
              href="/franchise"
              target="_blank"
              className="text-white hover:text-[#1466D4] cursor-pointer"
            >
              Franchise
            </a>
            <span className="text-white">|</span>
            <a
              href="https://drive.google.com/file/d/19AEqpT_aszYBo8RxES6KsIzH_RiOFH-i/view?usp=sharing"
              download
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-[#1466D4] cursor-pointer"
            >
              Privacy Policy
            </a>
            <span className="text-white">|</span>
            <a
              href="https://drive.google.com/file/d/184xj4cMqj9xV8sWHvNnCyhVtKaVAOHfD/view?usp=sharing"
              download
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-[#1466D4] cursor-pointer"
            >
              Terms of Service
            </a>
          </nav>

          {/* Middle Row: Social Icons */}
          <div className="flex justify-center space-x-4 mb-2">
            <a
              href="https://www.instagram.com/karshuttle"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/instagram_logo.svg"
                alt="Instagram"
                className="w-5 h-5"
              />
            </a>
          </div>

          {/* KAR Login on mobile */}
          <button
            onClick={handleKarLogin}
            className="inline-flex items-center rounded-full border border-white/30 px-4 py-1.5 text-sm font-semibold hover:bg-white hover:text-black transition mb-2"
            type="button"
          >
            KAR Login
          </button>

          {/* Bottom Row: Copyright */}
          <div className="text-[10px] text-center">
            © 2025 KAR Shuttles Ltd. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
